/* ============================================================
   Atelier cart drawer - functional controller
   ------------------------------------------------------------
   Server-rendered markup (snippets/atelier-cart.liquid), re-rendered live via
   the Section Rendering API (sections/atelier-cart.liquid). Handles:
   open/close + scroll-lock, optimistic quantity/remove (instant UI, batched
   server sync), note, the collapsible tools, the live shipping estimate, an
   in-flight progress bar, and a 429-tolerant fetch wrapper.

   window.atelierCartOn gates routing; window.openAtelierCart() is the entry.
   ============================================================ */
(function () {
  var drawer, scrim;
  var pending = {};      // { lineKey: targetQty } awaiting a batched sync
  var flushing = false;  // a /cart/update.js is in flight
  var inflight = 0;      // add / refresh / shipping requests in flight
  var flushTimer = null;
  var shipSeq = 0;       // latest delivery-quote request; older responses are ignored
  var shipLoc = null;    // locality the on-screen quote (or the saved one) is for
  var shipSig = null;    // cart signature the on-screen quote was priced for
  var rqTimer = null;

  /* quantity engine state - see "quantity engine" below */
  var epoch = 0;           // bumped by every edit and every write: a read that started earlier is old news
  var sending = null;      // { lineKey: qty } in the save now in flight
  var flushQueued = false; // a save is waiting its turn and will take everything pending
  var writing = 0;         // cart writes running or waiting in serial()
  var writeTail = Promise.resolve();
  var lines = {};          // latest server picture: { lineKey: variantId }
  var ident = {};          // lineKey -> 'variantId|properties', from cart JSON
  var retryTimer = null, retries = 0, refusals = 0, forcePreflight = false, lastSaveOk = true;
  var RETRY_MS = [700, 1500, 3000, 5000, 8000];

  /* price engine state - see "price engine" below */
  var tierTrust = null;    // CornerCart's tiers vs Shopify's prices: null = not judged yet, true = they match, false = they did not (off for this page)
  var tierAwait = false;   // Shopify's latest picture had not caught up with the tier its total earns
  var tierTimer = null;
  var tierMomentAt = 0;    // when the milestone moment last started (a redraw carries it on)
  var lastWriteAt = 0;     // when our last cart write finished
  var discGoing = false;   // a discount code is being taken off; the figures wait for Shopify
  var lastHtml = null;     // the latest server render (shown as it is when a tier prediction is dropped)

  /* ---------- small helpers ---------- */
  function lockScroll() {
    var sw = window.innerWidth - document.documentElement.clientWidth;
    if (sw > 0) document.body.style.paddingRight = sw + 'px';
    document.body.style.overflow = 'hidden';
  }
  function unlockScroll() { document.body.style.overflow = ''; document.body.style.paddingRight = ''; }
  function debounce(fn, ms) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); }; }
  function rootUrl() { return (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/'; }
  function esc(s) { return (s || '').replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  /* Retry on 429 (dev proxy + multiple cart apps can rate-limit a burst). */
  function cartFetch(url, opts, tries) {
    tries = (tries == null) ? 3 : tries;
    return fetch(url, opts).then(function (r) {
      if (r.status === 429 && tries > 0) {
        return new Promise(function (res) { setTimeout(res, 700); }).then(function () { return cartFetch(url, opts, tries - 1); });
      }
      return r;
    });
  }

  /* Shopify money formatting (optimistic prices, client-side) */
  function formatMoney(cents) {
    var fmt = (window.atelierMoneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    function fmtNum(c, precision, thousands, decimal) {
      if (isNaN(c)) c = 0;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      precision = precision == null ? 2 : precision;
      var n = (c / 100).toFixed(precision);
      var parts = n.split('.');
      var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }
    var pat = /\{\{\s*(\w+)\s*\}\}/;
    var m = fmt.match(pat);
    if (!m) return fmt;
    var value;
    switch (m[1]) {
      case 'amount_no_decimals': value = fmtNum(cents, 0); break;
      case 'amount_with_comma_separator': value = fmtNum(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmtNum(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = fmtNum(cents, 2, ' ', '.'); break;
      case 'amount_with_apostrophe_separator': value = fmtNum(cents, 2, "'", '.'); break;
      default: value = fmtNum(cents, 2);
    }
    return fmt.replace(pat, value);
  }

  function refreshBar() {
    if (drawer) drawer.classList.toggle('loading', busy() || inflight > 0);
  }

  /* ---------- open / close ---------- */
  function bind() {
    drawer = document.getElementById('AtelierCart');
    scrim = document.getElementById('AtelierCartScrim');
    if (!drawer || !scrim) return false;
    if (drawer.dataset.bound) return true;
    drawer.dataset.bound = '1';
    scrim.addEventListener('click', close);
    drawer.addEventListener('click', onClick);
    drawer.addEventListener('submit', onSubmit);
    drawer.addEventListener('input', onInput);
    drawer.addEventListener('focusin', function (e) { if (e.target.id === 'AtelierShipQ') loadLocs(); });
    drawer.addEventListener('focusout', function (e) { if (e.target.id === 'AtelierShipQ') setTimeout(function () { var l = document.getElementById('AtelierShipList'); if (l) l.classList.remove('on'); }, 150); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && drawer.classList.contains('on')) close(); });
    drawer.addEventListener('keydown', function (e) { if (e.key === 'Enter' && e.target.id === 'AtelierDiscInput') { e.preventDefault(); applyDiscount(); } });
    lines = readLines(drawer);   // the server-rendered drawer is the first server picture
    judgeTier();
    updateGoalMeter();
    return true;
  }
  function show() {
    if (!bind()) return;
    scrim.classList.add('on');
    drawer.classList.add('on');
    drawer.setAttribute('aria-hidden', 'false');
    lockScroll();
  }
  function open() { if (!bind()) return; show(); refresh().then(function () { cleanupOrphans().then(requote); }); }
  function close() {
    if (!drawer) return;
    drawer.classList.remove('on');
    scrim.classList.remove('on');
    drawer.setAttribute('aria-hidden', 'true');
    unlockScroll();
  }

  /* ---------- render ---------- */
  /* Every server render goes through here. It is the truth for the whole cart,
     then the edits it does not include yet (still waiting, or in the save now in
     flight) are laid back over it - so no reply can put back a number the
     customer has already changed. The tier CornerCart is still catching up on is
     laid over it the same way (see "price engine"). */
  function applySection(html, opts) {
    if (!drawer) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.getElementById('AtelierCart');
    if (!fresh) return;
    lastHtml = html;
    lines = readLines(fresh);
    remap(lines);
    var prevLbl = tierLbl();
    swapIn(fresh, opts);
    judgeTier();   // Shopify's own picture, before our edits go back over it
    if (overlayEdits() || tierAwait || discGoing) recompute(prevLbl);
    else { paintUnsure(); tierMoment(prevLbl); }
  }
  function swapIn(fresh, opts) {
    var itemsEl = drawer.querySelector('[data-ct-items]');
    if (opts && opts.noAnim && itemsEl) itemsEl.classList.add('no-anim');
    var keep = discState(drawer.querySelector('#AtelierDiscInput'), drawer.querySelector('#AtelierDiscMsg'));
    /* the cart V2 overlay's delivery row lives inside the summary: carry it across
       the redraw, or it vanishes for a frame or two until the overlay re-adds it */
    var dRow = drawer.querySelector('[data-ct-summary] .cro-delivery-row');
    ['[data-ct-items]', '[data-ct-summary]', '[data-ct-disc]'].forEach(function (sel) {
      var f = fresh.querySelector(sel), c = drawer.querySelector(sel);
      if (f && c && f.innerHTML !== c.innerHTML) c.innerHTML = f.innerHTML;
    });
    if (dRow && !dRow.isConnected) {
      var dTot = drawer.querySelector('[data-ct-summary] .ct-tot');
      if (dTot) dTot.parentNode.insertBefore(dRow, dTot);
    }
    restoreDisc(keep, drawer.querySelector('#AtelierDiscInput'), drawer.querySelector('#AtelierDiscMsg'));
    ['[data-ct-count]', '[data-ct-total]', '[data-ct-disc-lbl]'].forEach(function (sel) {
      var f = fresh.querySelector(sel), c = drawer.querySelector(sel);
      if (f && c && f.textContent !== c.textContent) c.textContent = f.textContent;
    });
    drawer.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
    if (opts && opts.noAnim && itemsEl) requestAnimationFrame(function () { itemsEl.classList.remove('no-anim'); });
    updateGoalMeter();
    croNow();
  }
  /* the cart V2 overlay relabels the summary (atelier-cart-cro.js): run it now, in
     this frame, rather than on its observer's next frame */
  function croNow() { if (typeof window.BSDCartCroEnhance === 'function') { try { window.BSDCartCroEnhance(); } catch (e) { /* overlay's own problem */ } } }

  /* The discount panel is redrawn with the rest of the drawer, so a background
     redraw used to wipe a code the customer was still typing (and the "isn't
     valid" message). Carry both across; only an EMPTY fresh field or message is
     filled, so a redraw that means to change them (code applied) still wins. */
  function discState(input, msg) {
    return {
      input: input, typed: input ? input.value : '', focused: !!input && document.activeElement === input,
      caret: input && document.activeElement === input ? input.selectionStart : null,
      msg: msg, text: msg ? msg.textContent : '', cls: msg ? msg.className : ''
    };
  }
  function restoreDisc(k, input, msg) {
    if (input && input !== k.input && k.typed && !input.value) {
      input.value = k.typed;
      if (k.focused) { input.focus(); try { input.setSelectionRange(k.caret, k.caret); } catch (e) { /* not a text field */ } }
    }
    if (msg && msg !== k.msg && k.text && !msg.textContent) { msg.textContent = k.text; msg.className = k.cls; }
  }

  /* force: show Shopify's cart even while a save is pending (only used after a
     save has been given up) - a newer edit still wins over it.
     Resolves true when the picture was shown. */
  function refresh(quiet, force, tries) {
    var start = epoch;
    if (!quiet) { inflight++; refreshBar(); }
    return cartFetch(rootUrl() + '?section_id=atelier-cart')
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return false;
        /* a picture taken before the latest edit or save is old news: dropping it is
           what stops a background check putting back a number the customer changed */
        if (start !== epoch || (!force && busy())) { refreshAgain(quiet, tries); return false; }
        applySection(html);
        return true;
      })
      .catch(function (e) { console.error('[atelier-cart] refresh', e); return false; })
      .finally(function () { if (!quiet) { inflight--; refreshBar(); } });
  }

  /* A dropped picture is replaced by a newer one, never just discarded - once the
     cart is quiet, read it again (up to 3 times). 2026-10-06 live bug: two
     openAtelierCart() calls after an add (the theme's add paths each open the
     drawer); the first one's orphan check marked the cart changed, the second
     one's (correct) picture was dropped, and nothing redrew - "0 items" until the
     drawer was reopened. A save's own reply still renders itself; reading again
     after it is harmless (it is the latest picture). */
  function refreshAgain(quiet, tries) {
    tries = (tries == null) ? 3 : tries;
    if (tries <= 0) return;
    (function wait(n) {
      if (busy() && n > 0) { setTimeout(function () { wait(n - 1); }, 150); return; }
      refresh(quiet, false, tries - 1);
    })(60);
  }

  /* CornerCart applies/updates its tier offer AFTER our mutation round-trip:
     its engine sees our /cart call land, then writes its own cart attribute,
     then the discount function reprices - so the section render inside a
     mutation response is one beat behind on tier crossings (row missing on
     the first crossing, stale "5%" on the next; Chris-reported 2026-08-24).
     Settle checks = two quiet re-fetches after each mutation settles, picking
     up the offer without a manual reload; applySection's innerHTML compare
     makes them no-ops when nothing changed. Gated on an active goal campaign
     (or a rendered row that may need retiring) so carts run ZERO extra
     requests while no tiers are configured; probes paced wide apart for the
     dev proxy's burst rate-limiting. */
  var settleTimers = [];
  function scheduleSettle() {
    if (!drawer) return;
    if (!goalCampaign() && !drawer.querySelector('[data-ct-tier-row]')) return;
    settleTimers.forEach(clearTimeout);
    settleTimers = [];
    [1000, 2200, 3600].forEach(function (ms) {
      settleTimers.push(setTimeout(function () {
        if (busy()) return;  // mid-edit: the next save reschedules
        refresh(true);
      }, ms));
    });
  }

  /* ---------- optimistic quantity / remove ---------- */
  /* prevLbl: the tier label before this change (applySection passes the one
     shown before its redraw) - see tierMoment() */
  function recompute(prevLbl) {
    if (prevLbl === undefined) prevLbl = tierLbl();
    var sub = 0, save = 0, count = 0;
    drawer.querySelectorAll('.ct-item').forEach(function (it) {
      var q = parseInt(it.getAttribute('data-qty'), 10) || 0;
      var u = +it.getAttribute('data-unit-price') || 0;
      var c = +it.getAttribute('data-unit-compare') || 0;
      sub += u * q; count += q;
      if (c > u) save += (c - u) * q;
    });
    /* the tier this cart earns, exactly as Shopify will charge it - only while
       CornerCart's tiers have matched Shopify's prices on this page */
    var p = pic();
    if (tierPredicts(p)) setTier(earned(tierModel(), sub), sub);
    /* order-level portion of applied discounts (code row + automatic-sale row
       + CornerCart tier milestone row, all server-rendered); line-level
       allocations are already inside data-unit-price */
    var disc = 0;
    drawer.querySelectorAll('[data-ct-disc-row],[data-ct-auto-row],[data-ct-tier-row]').forEach(function (r) { disc += +r.getAttribute('data-disc') || 0; });
    disc = Math.min(disc, sub);
    var subEl = drawer.querySelector('[data-ct-summary] .ct-sub .val'); if (subEl) subEl.textContent = formatMoney(sub);
    /* data-cents mirrors the text (the cart V2 overlay reads it as the goods figure) */
    var grand = drawer.querySelector('[data-ct-summary] .ct-tot .val'); if (grand) { grand.textContent = formatMoney(sub - disc); grand.setAttribute('data-cents', String(sub - disc)); }
    /* grand savings line = markdown savings + discounts (approximation until
       the server re-render lands with exact allocations). Visible only while
       MARKDOWN savings exist - mirrors the Liquid atc_saved gate, so the row
       never merely echoes the itemized rows above it */
    var saveTotal = save + disc;
    var sr = drawer.querySelector('[data-ct-summary] .ct-save-row');
    if (sr) { if (save > 0) { sr.classList.add('on'); var b = sr.querySelector('b'); if (b) b.textContent = formatMoney(saveTotal); } else sr.classList.remove('on'); }
    var co = drawer.querySelector('[data-ct-total]'); if (co) co.textContent = 'Checkout · ' + formatMoney(sub - disc);
    var cnt = drawer.querySelector('[data-ct-count]'); if (cnt) cnt.textContent = count + ' ' + (count === 1 ? 'item' : 'items');
    syncBadge(count);
    drawer.classList.toggle('is-empty', count === 0);
    markShipStale();
    paintUnsure(p);
    tierMoment(prevLbl);
    updateGoalMeter();
    croNow();
  }
  /* The estimate on screen was priced for an earlier cart the moment the cart
     changes: flag it .stale on the tap ("Updating delivery") instead of after the
     save and the re-quote start (Chris 2026-10-06: the old figure sat there for
     1.5-2 s). Back to the cart it was priced for = no longer stale. */
  function markShipStale() {
    var ship = document.getElementById('AtelierShip');
    if (!ship || !ship.classList.contains('done') || shipSig === null) return;
    if (cartSig() !== shipSig) ship.classList.add('stale');
    else if (!ship.classList.contains('loading')) ship.classList.remove('stale');
  }
  function updateLine(it) {
    var q = parseInt(it.querySelector('.ct-qty .n').textContent, 10) || 0;
    var u = +it.getAttribute('data-unit-price') || 0;
    var c = +it.getAttribute('data-unit-compare') || 0;
    var now = it.querySelector('.ct-pr .now'); if (now) now.textContent = formatMoney(u * q);
    var was = it.querySelector('.ct-pr .was'); if (was && c > u) was.textContent = formatMoney(c * q);
  }
  /* ---------- quantity engine ----------
     Chris, 2026-10-02: once a customer changes a quantity or removes an item the
     drawer keeps their choice - nothing in the background may change it back -
     and Shopify ends up with exactly what they chose. Measured on live before
     this (lab: _cart-drawer-rig/lab): +7 clicks during a save left the cart at 4,
     not 8 (the save's reply was drawn over the newer clicks and the next click
     counted on from it); removed lines came back for 1-2 s; checkout clicked
     within ~0.45 s of the last click opened with the old quantity.
     - serial(): every cart write we make runs one at a time - Shopify applies
       simultaneous writes in no fixed order (a burst of 10 ended on the FIRST).
     - Edits wait in `pending` for a 450 ms merge window, then one save sends the
       latest quantities; clicks made while it is in flight go in the next one.
     - applySection() lays unsaved edits back over every render; refresh() drops
       any read that started before the latest edit or save.
     - Line keys change whenever the CornerCart tier reprices a line, so pending
       keys are re-resolved against the latest server picture before sending
       (remap / liveKey), and a key for a line that has gone is never sent -
       Shopify rejects the WHOLE batch for one.
     - A save that fails for a passing reason (network, busy, bot check) is
       retried with the customer's numbers kept; only a real refusal shows
       Shopify's cart again, with a message (giveUp).
     - Checkout / "View full cart" with an unsaved edit: save it, then go
       (leaveWhenSaved). A form post carrying the quantities was measured unsafe:
       it silently ignores a key the tier has changed and errors on a removed one. */
  var MSG_CHANGED = 'An item in your cart changed while you were editing - please check your cart.';
  var MSG_ADJUSTED = 'Some quantities changed when your cart was saved - please check them.';
  var MSG_FAILED = 'We couldn\'t save your last change, so your cart is shown as it is saved. Please try again.';
  var lostEdit = false, notifyTimer = null, leaving = null;

  function noop() {}
  function hasPending() { for (var k in pending) return true; return false; }
  function busy() { return flushing || flushQueued || writing > 0 || !!retryTimer || hasPending(); }

  /* run cart writes one at a time, in the order they were asked for. readOnly: a
     task that usually only reads (the orphan check) keeps its place in the queue
     but does not mark the cart changed - it bumps epoch itself if it does write */
  function serial(task, readOnly) {
    var bump = readOnly ? 0 : 1;   // a read-only task does not hold up renders either (it is not `writing`)
    writing += bump; epoch += bump; refreshBar();
    var run = writeTail.then(function () { return task(); });
    writeTail = run.then(noop, noop);
    return run.then(function (v) { writing -= bump; epoch += bump; if (bump) lastWriteAt = Date.now(); refreshBar(); return v; },
      function (e) { writing -= bump; epoch += bump; if (bump) lastWriteAt = Date.now(); refreshBar(); throw e; });
  }

  function vOf(key) { return String(key).split(':')[0]; }
  function readLines(scope) {
    var pic = {};
    scope.querySelectorAll('.ct-item').forEach(function (li) { var k = li.getAttribute('data-key'); if (k) pic[k] = vOf(k); });
    return pic;
  }
  /* cart JSON -> server picture; also records each line's identity (variant +
     properties), which survives the key changing */
  function noteCart(cart) {
    if (!cart || !cart.items) return null;
    var pic = {};
    cart.items.forEach(function (it) {
      pic[it.key] = String(it.variant_id);
      ident[it.key] = it.variant_id + '|' + JSON.stringify(it.properties || {});
    });
    return pic;
  }
  /* the key an edit should go out with: its own while the line still has it,
     else the one line with the same identity, else the one line of that variant;
     null when the line has left the cart or cannot be told apart */
  function liveKey(key, pic) {
    if (pic[key]) return key;
    var v = vOf(key), id = ident[key], byId = [], byV = [];
    for (var k in pic) {
      if (pic[k] !== v) continue;
      byV.push(k);
      if (id && ident[k] === id) byId.push(k);
    }
    if (byId.length === 1) return byId[0];
    if (byV.length === 1 && (!id || !ident[byV[0]] || ident[byV[0]] === id)) return byV[0];
    return null;
  }
  function remap(pic) {
    [pending, sending].forEach(function (map) {
      if (!map) return;
      Object.keys(map).forEach(function (k) {
        if (pic[k]) return;
        var q = map[k], nk = liveKey(k, pic);
        delete map[k];
        if (nk) { if (!(nk in map)) map[nk] = q; }
        else if (q > 0) lostEdit = true;   // its line has left the cart: nothing to apply it to (a removal is simply done)
      });
    });
  }
  /* lay the edits Shopify has not confirmed yet over the drawer */
  function overlayEdits() {
    var edits = {}, any = false, k;
    if (sending) for (k in sending) { edits[k] = sending[k]; any = true; }
    for (k in pending) { edits[k] = pending[k]; any = true; }
    if (!any) return false;
    drawer.querySelectorAll('.ct-item').forEach(function (li) {
      var key = li.getAttribute('data-key');
      if (!key || !(key in edits)) return;
      var q = edits[key];
      if (q <= 0) { li.remove(); return; }
      var n = li.querySelector('.ct-qty .n');
      if (!n) return;
      li.setAttribute('data-qty', q);
      n.textContent = q;
      updateLine(li);
    });
    if (lostEdit) { lostEdit = false; notify(MSG_CHANGED); }
    return true;
  }
  function notify(text) {
    var foot = drawer && drawer.querySelector('.ct-foot');
    if (!foot) return;
    var el = foot.querySelector('.ct-sync-msg');
    if (!el) {
      el = document.createElement('p');
      el.className = 'ct-disc-msg err ct-sync-msg';
      el.setAttribute('role', 'status');
      el.style.margin = '0 0 10px';
      foot.insertBefore(el, foot.firstChild);
    }
    el.textContent = text;
    clearTimeout(notifyTimer);
    notifyTimer = setTimeout(function () { el.textContent = ''; }, 9000);
  }

  function queue(key, qty) { pending[key] = qty; epoch++; scheduleFlush(); }
  function scheduleFlush() { clearTimeout(flushTimer); flushTimer = setTimeout(flush, 450); refreshBar(); }
  function flush() {
    clearTimeout(flushTimer); flushTimer = null;
    if (!hasPending() || flushQueued) { refreshBar(); return; }
    flushQueued = true;
    serial(sendPending).catch(function (e) { console.error('[atelier-cart] save', e); });
  }
  function readCart() {
    return fetch(rootUrl() + 'cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }
  /* re-read the cart before sending when a key could be ambiguous: an edit on a
     line whose key we have not seen from the server, or on a variant that sits
     on more than one line (the variant fallback could then hit the wrong one) */
  function needsPreflight() {
    var count = {}, k;
    for (k in lines) count[lines[k]] = (count[lines[k]] || 0) + 1;
    for (k in pending) if (!lines[k] || count[vOf(k)] > 1) return true;
    return false;
  }
  function postUpdates(updates) {
    return fetch(rootUrl() + 'cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates, sections: 'atelier-cart', sections_url: location.pathname })
    }).then(function (r) {
      /* judged by the body, not the Content-Type (Shopify's .js endpoints are not
         labelled json): a reply that is not JSON - a bot-check page, a proxy error -
         is a passing failure, even with a 200 */
      return r.text().then(function (t) {
        var j = null;
        try { j = JSON.parse(t); } catch (err) { /* not JSON */ }
        if (!j) return { ok: false, status: r.ok ? 0 : (r.status || 0) };
        return { ok: r.ok, status: r.status, data: j };
      }, function () { return { ok: false, status: 0 }; });
    }, function () { return { ok: false, status: 0 }; });
  }
  function sendPending() {
    flushQueued = false;
    if (!hasPending()) return Promise.resolve();
    var pre = (forcePreflight || needsPreflight()) ? readCart() : Promise.resolve(null);
    return pre.then(function (cart) {
      forcePreflight = false;
      var pic = noteCart(cart);
      if (pic) lines = pic;
      remap(lines);
      if (lostEdit) { lostEdit = false; notify(MSG_CHANGED); }
      if (!hasPending()) return;   // nothing left to send (a removal that has already landed)
      var sent = sending = pending;
      pending = {};
      flushing = true; refreshBar();
      var hadRemoval = false;
      for (var rk in sent) { if (sent[rk] === 0) hadRemoval = true; }
      return postUpdates(sent).then(function (res) {
        flushing = false;
        if (!res.ok || !res.data) { failed(sent, res); return; }
        sending = null; retries = 0; refusals = 0; lastSaveOk = true;
        var data = res.data, html = data.sections && data.sections['atelier-cart'];
        var pic2 = noteCart(data);   // identities first, so edits made meanwhile follow a changed key
        if (html) applySection(html, { noAnim: true });
        else { if (pic2) { lines = pic2; remap(lines); } setTimeout(function () { refresh(true); }, 0); }
        checkAdjusted(sent, data);
        if (!hasPending()) syncBadge(data.item_count);
        scheduleSettle();
        /* a removal may orphan Vanity Builder children: price the cart AFTER the
           cleanup has settled it, never the transient state in between. Queued
           behind this save by serial(), never awaited inside it. */
        if (hadRemoval) cleanupOrphans().then(requote); else requote();
      });
    }).catch(function (e) {
      flushing = false;
      console.error('[atelier-cart] save', e);
      if (sending) failed(sending, { ok: false, status: 0 });
    });
  }
  /* a save did not land: keep the customer's numbers (newer edits win) and retry,
     or - for a real refusal that survives a fresh read of the cart - give up */
  function failed(sent, res) {
    sending = null;
    var merged = {}, k;
    for (k in sent) merged[k] = sent[k];
    for (k in pending) merged[k] = pending[k];
    pending = merged;
    console.warn('[atelier-cart] save not applied', res.status, res.data && (res.data.description || res.data.message));
    if (res.status === 400 || res.status === 404 || res.status === 422) {
      if (++refusals <= 1) { forcePreflight = true; flush(); return; }   // usually a key that changed: re-read, resend once
      giveUp();
      return;
    }
    if (retries >= RETRY_MS.length) { giveUp(); return; }
    clearTimeout(retryTimer);
    retryTimer = setTimeout(function () { retryTimer = null; flush(); }, RETRY_MS[retries++]);
    refreshBar();
  }
  function giveUp() {
    clearTimeout(retryTimer); retryTimer = null;
    pending = {}; sending = null; retries = 0; refusals = 0; lastSaveOk = false;
    notify(MSG_FAILED);
    setTimeout(function () { refresh(false, true); }, 0);   // Shopify's cart, as it really is
  }
  /* Shopify answered with a different quantity than we sent and the customer has
     not edited that line since - its render (already shown) is the truth; say so.
     Exact key or a unique identity only: a guess must never raise a false alarm. */
  function checkAdjusted(sent, cart) {
    var pic = noteCart(cart);
    if (!pic) return;
    var qty = {};
    cart.items.forEach(function (it) { qty[it.key] = it.quantity; });
    for (var k in sent) {
      var nk = pic[k] ? k : null;
      if (!nk && ident[k]) {
        var m = Object.keys(pic).filter(function (x) { return ident[x] === ident[k]; });
        if (m.length === 1) nk = m[0];
      }
      if (!nk || (nk in pending) || (k in pending)) continue;   // gone (a removal landed), unknown, or edited again since
      if (qty[nk] !== sent[k]) { notify(MSG_ADJUSTED); return; }
    }
  }

  /* resolves once every edit has reached Shopify and the tier has settled (true),
     or false when a save was given up or this takes longer than `ms`. The tier
     gets 4 s: CornerCart writes its tier from this page, so leaving first can
     send checkout out with the old one. */
  function settled(since) { return !busy() && !discGoing && (!tierAwait || Date.now() - since > 4000); }
  function whenIdle(ms) {
    flush();
    var t0 = Date.now();
    return new Promise(function (resolve) {
      (function check() {
        if (settled(t0)) { resolve(lastSaveOk); return; }
        if (Date.now() - t0 > ms) { resolve(false); return; }
        setTimeout(check, 50);
      })();
    });
  }
  /* Checkout / "View full cart" while an edit has not reached Shopify yet: hold
     the click, let the save land, then go - so the next page shows exactly what
     the drawer showed. Nothing waiting = returns false and the link works as it
     always has. */
  function leaveWhenSaved(e, el, href) {
    if (settled(Date.now())) return false;
    if (e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) { flush(); return false; }   // new tab/window: send now, let it open
    e.preventDefault();
    if (leaving) return true;
    leaving = el;
    /* .is-saving swaps the label to "Saving your cart..." in CSS (the cart V2 overlay
       rewrites the drawer button's text, so the text itself is left alone) */
    el.setAttribute('aria-busy', 'true'); el.classList.add('is-saving');
    el.style.opacity = '.85'; el.style.pointerEvents = 'none'; el.style.cursor = 'progress';
    whenIdle(15000).then(function (ok) {
      if (ok) { window.location.href = href; return; }
      resetLeaving();
      if (lastSaveOk) notify('Still saving your cart - please try again in a moment.');   // a give-up has already said why
    });
    return true;
  }
  function resetLeaving() {
    if (!leaving) return;
    leaving.removeAttribute('aria-busy'); leaving.classList.remove('is-saving');
    leaving.style.opacity = ''; leaving.style.pointerEvents = ''; leaving.style.cursor = '';
    leaving = null;
  }
  /* Back from checkout restores this page from the back/forward cache with the
     button still held - release it */
  window.addEventListener('pageshow', function (ev) { if (ev.persisted) resetLeaving(); });
  /* leaving the page any other way (a header link, closing the tab) inside the
     merge window: send what is waiting with keepalive so it survives the page
     (measured: a keepalive save fired as the page leaves lands) */
  window.addEventListener('pagehide', function () {
    clearTimeout(flushTimer);
    if (!hasPending()) return;
    var body = {}, any = false;
    for (var k in pending) { var nk = liveKey(k, lines); if (nk) { body[nk] = pending[k]; any = true; } }
    pending = {};
    if (!any) return;
    try {
      fetch(rootUrl() + 'cart/update.js', {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates: body })
      });
    } catch (err) { /* the page is going; nothing more to do */ }
  });
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden' && hasPending()) flush(); });

  /* ---------- price engine ----------
     Chris, 2026-10-06: the cart must never show a wrong price, and should show
     the right one on the click. The quantity engine makes quantities right on
     the click; this does the same for the money.
     - CornerCart's goal tier (Spend & Save) is a percentage off the whole order,
       chosen by the item total. Shopify only reprices it once CornerCart has seen
       our save and written its own change (1-3.6 s), so the drawer works the tier
       out itself (setTier) and lays it over every reply that has not caught up.
     - It predicts only while CornerCart's tiers have matched what Shopify charged
       on this page (judgeTier, on every server picture). The right tier with a
       different amount means our sum is not Shopify's: prediction stops at once.
       A different tier means CornerCart has not caught up yet - unless that
       lasts ~4.5 s past our last write, which stops prediction too. Stopped =
       Shopify's figures for the rest of the page.
     - A figure that cannot be worked out exactly - a discount code or automatic
       discount (their rules live in Shopify), a line with a product-level
       discount (Shopify rounds its unit price), the tier when not predicting -
       is drawn as a placeholder (.atc-pend) until Shopify answers, never as a
       stale number (paintUnsure).
     - Checkout / "View full cart" also wait (up to 4 s) for the tier to settle,
       so checkout opens with the discount the drawer showed. */
  function tierLbl() { var l = drawer && drawer.querySelector('[data-ct-tier-row] .lbl'); return l ? l.textContent : null; }
  /* the running CornerCart tiers as [{ goal (cents), pct }], or null when they
     are not one whole-order percentage each - nothing we can work out exactly */
  function tierModel() {
    var d = goalCampaign();
    if (!d) return null;
    var out = [];
    for (var i = 0; i < d.milestones.length; i++) {
      var mi = d.milestones[i], st = mi && mi.rewards && mi.rewards.rewardStack;
      if (!st || st.length !== 1 || st[0].unit !== 'percent' || (st[0].type && st[0].type !== 'orderDiscount')) return null;
      var pct = +st[0].value, goal = +mi.goal;
      if (!(pct > 0 && pct < 100) || !(goal >= 0)) return null;
      out.push({ goal: Math.round(goal * 100), pct: pct });
    }
    return out.sort(function (a, b) { return a.goal - b.goal; });
  }
  function earned(model, base) { var pct = 0; model.forEach(function (t) { if (base >= t.goal) pct = t.pct; }); return pct; }
  function pctOff(base, pct) { return Math.round(base * pct / 100); }
  function editMap() { var m = {}, k; if (sending) for (k in sending) m[k] = 1; for (k in pending) m[k] = 1; return m; }
  /* what the drawer shows now: the item total, its tier row, and what could
     make its figures inexact */
  function pic() {
    var edits = editMap(), p = { base: 0, ld: false, ldEdit: false };
    drawer.querySelectorAll('.ct-item').forEach(function (it) {
      p.base += (+it.getAttribute('data-unit-price') || 0) * (parseInt(it.getAttribute('data-qty'), 10) || 0);
      if (it.hasAttribute('data-ldisc')) { p.ld = true; if (it.getAttribute('data-key') in edits) p.ldEdit = true; }
    });
    var row = drawer.querySelector('[data-ct-tier-row]');
    p.row = !!row;
    p.pct = row ? parseFloat(row.getAttribute('data-pct')) : 0;   // NaN = a tier that is not a percentage
    p.amt = row ? (+row.getAttribute('data-disc') || 0) : 0;
    p.other = !!drawer.querySelector('[data-ct-disc-row],[data-ct-auto-row]');
    return p;
  }
  /* Shopify's own picture (just drawn, before our edits go back over it): is
     its tier the one its total earns? */
  function judgeTier() {
    if (!drawer) return;
    var m = tierModel(), p = pic();
    if (!m || p.other || p.ld || tierTrust === false) { endAwait(); return; }
    if (p.row && isNaN(p.pct)) { stopPredicting('tier is not a percentage'); return; }
    var want = earned(m, p.base);
    if (want === p.pct) {
      if (want && pctOff(p.base, want) !== p.amt) { stopPredicting('amount ' + p.amt + ' != ' + pctOff(p.base, want)); return; }
      tierTrust = true; endAwait();
      return;
    }
    if (!tierAwait) { tierAwait = true; armTierCheck(); }   // CornerCart has not caught up yet
  }
  function tierPredicts(p) { return tierTrust === true && !discGoing && !p.other && !p.ld && !!tierModel(); }
  function stopPredicting(why) {
    tierTrust = false; endAwait();
    console.warn('[atelier-cart] tier prediction off for this page:', why);
  }
  function endAwait() { tierAwait = false; clearTimeout(tierTimer); tierTimer = null; }
  function armTierCheck() {
    clearTimeout(tierTimer);
    tierTimer = setTimeout(function () {
      tierTimer = null;
      if (!tierAwait) return;
      if (editsInFlight() || writing > 0 || Date.now() - lastWriteAt < 4000) { armTierCheck(); return; }
      refresh(true).then(function (shown) {
        if (!tierAwait) return;                  // that picture agreed
        if (!shown) { armTierCheck(); return; }  // overtaken by an edit: look again later
        stopPredicting('tier did not settle');
        if (lastHtml) applySection(lastHtml);    // Shopify's figures, as they are
      });
    }, 4500);
  }
  var TIER_CHIP = '<span class="chip"><svg viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5"/></svg></span>';
  /* the tier row as Shopify will draw it (same markup as snippets/atelier-cart) */
  function setTier(pct, base) {
    var row = drawer.querySelector('[data-ct-tier-row]');
    if (!pct) { if (row) row.remove(); return; }
    var amt = pctOff(base, pct);
    if (!row) {
      var sum = drawer.querySelector('[data-ct-summary]');
      var at = sum && (sum.querySelector('.cro-delivery-row') || sum.querySelector('.ct-tot'));
      if (!at) return;
      row = document.createElement('div');
      row.className = 'ct-tier';
      row.setAttribute('data-ct-tier-row', '');
      row.innerHTML = TIER_CHIP + '<span class="lbl"></span><b class="amt"></b>';
      at.parentNode.insertBefore(row, at);
    }
    row.setAttribute('data-pct', String(pct));
    row.setAttribute('data-disc', String(amt));
    var l = row.querySelector('.lbl'), a = row.querySelector('.amt');
    var lt = pct + '% discount unlocked', at2 = '\u2212' + formatMoney(amt);
    if (l.textContent !== lt) l.textContent = lt;
    if (a.textContent !== at2) a.textContent = at2;
  }
  /* milestone moment: the tier row appeared, or moved to a different tier (the
     LABEL changed - "5%" -> "10%"; the amount alone changes on any qty edit and
     must not retrigger) - play the one-time settle + sheen. A row present
     unchanged since page load animates nothing: state, not an event. A redraw
     that replaces the row mid-moment carries it on from where it was (--jo). */
  function tierMoment(prevLbl) {
    var row = drawer.querySelector('[data-ct-tier-row]');
    if (!row) return;
    var lbl = row.querySelector('.lbl').textContent, age = 0;
    if (prevLbl === null || lbl !== prevLbl) tierMomentAt = Date.now();
    else {
      age = Date.now() - tierMomentAt;
      if (age >= 1300 || row.classList.contains('just-on')) return;
    }
    row.style.setProperty('--jo', (-age) + 'ms');
    row.classList.add('just-on');
    setTimeout(function () { row.classList.remove('just-on'); row.style.removeProperty('--jo'); }, 1300 - age);
  }
  function editsInFlight() { return hasPending() || !!sending || flushQueued || !!retryTimer; }
  /* true while a figure on screen could still change when Shopify answers */
  function unsure(p) {
    if (discGoing) return true;
    if (!editsInFlight() && !tierAwait) return false;
    if (p.other || p.ldEdit) return true;
    if (tierPredicts(p)) return false;
    return !!(p.row || tierModel());
  }
  function paintUnsure(p) {
    if (!drawer) return;
    p = p || pic();
    var on = unsure(p), edits = editMap();
    var sum = drawer.querySelector('[data-ct-summary]');
    if (sum) {
      sum.querySelectorAll('[data-ct-disc-row] .amt, [data-ct-auto-row] .amt, [data-ct-tier-row] .amt, .ct-save-row b').forEach(function (el) { el.classList.toggle('atc-pend', on); });
      /* (while delivery re-prices the total stays visible as the goods figure, labelled
         "Total before delivery" by the cart V2 overlay - atelier-cart-cro.js) */
      var tot = sum.querySelector('.ct-tot .val'); if (tot) tot.classList.toggle('atc-pend', on);
      var subv = sum.querySelector('.ct-sub .val'); if (subv) subv.classList.toggle('atc-pend', on && p.ldEdit);
      if (on) sum.setAttribute('aria-busy', 'true'); else sum.removeAttribute('aria-busy');
    }
    drawer.querySelectorAll('.ct-item').forEach(function (it) {
      var now = it.querySelector('.ct-pr .now');
      if (now) now.classList.toggle('atc-pend', on && it.hasAttribute('data-ldisc') && (it.getAttribute('data-key') in edits));
    });
  }

  /* ---------- badge ---------- */
  function syncBadge(count) {
    if (count == null) return;
    document.querySelectorAll('.fkcart-shortcode-count, .fkcart-item-count').forEach(function (el) {
      el.textContent = count; el.setAttribute('data-item-count', count);
    });
  }
  function syncBadgeFromDrawer() {
    var el = drawer && drawer.querySelector('[data-ct-count]');
    if (el) { var m = el.textContent.match(/\d+/); if (m) syncBadge(m[0]); }
  }

  /* Vanity Builder orphan cleanup — remove child add-ons whose parent vanity
     was removed (mirrors the old CornerCart safeguard). A parent is any line
     with a 'Cabinet Finish' property; a child ('Added with') is orphaned if its
     value matches no parent's product_title or handle. */
  var cleanupRunning = false;
  function cleanupOrphans() {
    if (cleanupRunning) return Promise.resolve();
    cleanupRunning = true;
    /* read + write inside serial(): it sees the cart after any save ahead of it,
       and its own write never overlaps another */
    return serial(function () { return cartFetch(rootUrl() + 'cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        noteCart(cart);
        var parents = {};
        (cart.items || []).forEach(function (it) {
          var p = it.properties || {};
          if (p['Cabinet Finish']) { if (it.product_title) parents[it.product_title] = 1; if (it.handle) parents[it.handle] = 1; }
        });
        var updates = {}, any = false;
        (cart.items || []).forEach(function (it) {
          var aw = (it.properties || {})['Added with'];
          if (aw && !parents[aw]) { updates[it.key] = 0; any = true; }
        });
        if (!any) return;
        console.log('[atelier-cart] removing orphaned Vanity Builder children');
        epoch++; writing++; refreshBar();   // a real write: reads started before it are old news
        return cartFetch(rootUrl() + 'cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates: updates, sections: 'atelier-cart', sections_url: location.pathname })
        })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            if (data && data.sections && data.sections['atelier-cart']) applySection(data.sections['atelier-cart'], { noAnim: true });
            if (data) { if (!hasPending()) syncBadge(data.item_count); scheduleSettle(); requote(); }
          })
          .finally(function () { writing--; epoch++; lastWriteAt = Date.now(); refreshBar(); });
      }); }, true)   // read-only unless it finds orphans (it bumps epoch itself then)
      .catch(function (e) { console.warn('[atelier-cart] orphan cleanup', e); })
      .finally(function () { cleanupRunning = false; });
  }

  /* ---------- CornerCart goal meter (tiered discount bar) ----------
     Mirrors the CornerCart "goal_meter_in_cart" campaign in Atelier styling.
     READ-ONLY consumer: campaign state (tiers, copy templates, schedule,
     active/off) comes from window.corner.campaigns, which the CornerCart app
     embed injects into every page. Staff enable/disable the feature in the
     CornerCart admin and this bar follows automatically - there is no theme-side
     toggle. Discount codes are applied by CornerCart's own engine (it monitors
     /cart/add|update|change independently of its UI); we never create, apply or
     edit codes here. */
  function goalCampaign() {
    try {
      var arr = window.corner && window.corner.campaigns;
      if (!arr || !arr.length) return null;
      var now = Date.now();
      for (var i = 0; i < arr.length; i++) {
        var c = arr[i];
        if (!c || c.type !== 'goal_meter_in_cart' || c.surface !== 'cart_widget') continue;
        if (c.status !== 'active') continue;
        if (c.audience && c.audience.everyone !== true) continue; // targeted audiences: let CornerCart handle
        var s = c.schedule || {};
        if (s.startsAt && now < Date.parse(s.startsAt)) continue;
        if (s.endsAt && now > Date.parse(s.endsAt)) continue;
        var d = c.details || {};
        if (d.goalType !== 'totalOrderValue') continue;       // order-value goals only (current store usage)
        if (!d.milestones || !d.milestones.length) continue;
        return d;
      }
    } catch (e) { console.warn('[atelier-cart] goal campaign parse', e); }
    return null;
  }
  function fillTpl(tpl, map) {
    return String(tpl == null ? '' : tpl).replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(map, k) ? map[k] : '';
    });
  }
  /* "$1,234.00" -> "$1,234" when whole dollars (matches the CornerCart bar's copy) */
  function moneyTrim(cents) {
    return formatMoney(Math.max(0, Math.round(cents))).replace(/([.,]00)(?!\d)/, '');
  }
  function tierDiscountLabel(mi) {
    var rs = mi && mi.rewards && mi.rewards.rewardStack && mi.rewards.rewardStack[0];
    if (!rs) return '';
    return rs.unit === 'percent' ? rs.value + '%' : moneyTrim(rs.value * 100);
  }
  var GOAL_CHECK = '<svg viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5"/></svg>';
  /* same tag glyph as the drawer's "Discount code" tool, so locked tiers read as discounts */
  var GOAL_TAG = '<svg viewBox="0 0 24 24"><path d="M20.6 13.4 11 3.8a2 2 0 0 0-1.4-.6H4.6A1.6 1.6 0 0 0 3 4.8v5a2 2 0 0 0 .6 1.4l9.6 9.6a2 2 0 0 0 2.8 0l4.6-4.6a2 2 0 0 0 0-2.8Z"/><path d="M7.5 7.5h.01"/></svg>';
  function updateGoalMeter() {
    var mount = document.getElementById('AtelierCartGoal');
    if (!mount) return;
    var d = goalCampaign();
    var sub = 0, count = 0;
    if (drawer) drawer.querySelectorAll('.ct-item').forEach(function (it) {
      var q = parseInt(it.getAttribute('data-qty'), 10) || 0;
      sub += (+it.getAttribute('data-unit-price') || 0) * q;
      count += q;
    });
    if (!d || count === 0) { mount.hidden = true; mount.innerHTML = ''; return; }

    var tiers = d.milestones.slice().sort(function (a, b) { return a.goal - b.goal; });
    var cur = sub / 100;                                   // goals are whole dollars (goalUnit AUD)
    var achieved = 0;
    tiers.forEach(function (t) { if (cur >= t.goal) achieved++; });
    var next = achieved < tiers.length ? tiers[achieved] : null;

    /* headline: next tier's preGoalText, or the last tier's postGoalText when all are unlocked */
    var msgTier = next || tiers[tiers.length - 1];
    var msg = fillTpl((msgTier.content || {})[next ? 'preGoalText' : 'postGoalText'], {
      goal: '<b>' + moneyTrim((msgTier.goal - cur) * 100) + '</b>',
      discount: tierDiscountLabel(msgTier),
      current_status: moneyTrim(sub)
    });

    /* equal segments per tier (parity with the CornerCart bar's layout);
       fill = completed segments + progress within the current segment */
    var seg = 1 / tiers.length, fill;
    if (!next) fill = 1;
    else {
      var prev = achieved > 0 ? tiers[achieved - 1].goal : 0;
      var frac = (cur - prev) / (next.goal - prev || 1);
      fill = seg * achieved + seg * Math.max(0, Math.min(1, frac));
    }

    var stops = '', labels = '';
    tiers.forEach(function (t, i) {
      var x = ((i + 1) / tiers.length * 100).toFixed(3) + '%';
      var on = i < achieved ? ' on' : '';
      stops += '<span class="ct-goal-stop' + on + '" style="left:' + x + '">' + (on ? GOAL_CHECK : GOAL_TAG) + '</span>';
      labels += '<span class="ct-goal-lb' + on + '" style="left:' + x + '">' +
        fillTpl((t.content || {}).milestoneLabel, { discount: tierDiscountLabel(t) }) + '</span>';
    });

    mount.innerHTML =
      '<div class="ct-goal-msg" aria-live="polite">' + msg + '</div>' +
      '<div class="ct-goal-bar" role="img" aria-label="Discount progress: ' + achieved + ' of ' + tiers.length + ' tiers unlocked">' +
        '<span class="ct-goal-track"></span>' +
        '<span class="ct-goal-fill" style="width:' + (fill * 100).toFixed(2) + '%"></span>' +
        stops +
      '</div>' +
      '<div class="ct-goal-lbs">' + labels + '</div>';
    mount.hidden = false;
  }

  /* ---------- note ---------- */
  function saveNote(v) {
    serial(function () {
      return fetch(rootUrl() + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: v }) })
        .catch(function (e) { console.error('[atelier-cart] note', e); });
    });
  }

  /* ---------- discount code (applied IN cart, not at checkout) ----------
     POST /cart/update.js {discount: CODE} attaches the code to the cart
     server-side; '' removes all codes (single-code UX by design, so that is
     exactly "remove the code"). The re-rendered section is the truth for
     totals and applied state; the JSON response's discount_codes[].applicable
     (when present) additionally catches codes Liquid cannot see in-cart
     (e.g. shipping-only codes, which have no cart-level money effect). */
  function setDiscMsg(text, err) {
    var m = document.getElementById('AtelierDiscMsg');
    if (m) { m.className = 'ct-disc-msg' + (err ? ' err' : ''); m.textContent = text || ''; }
  }
  function closeTools() {
    drawer.querySelectorAll('.ct-tool').forEach(function (x) { x.classList.remove('on'); });
    drawer.querySelectorAll('.ct-tool-panel').forEach(function (p) { p.classList.remove('on'); });
    var w = document.getElementById('AtelierToolWrap'); if (w) w.classList.remove('open');
  }
  function postDiscount(value) {
    return cartFetch(rootUrl() + 'cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discount: value, sections: 'atelier-cart', sections_url: location.pathname })
    }).then(function (r) {
      return r.json().catch(function () { return null; }).then(function (j) { return { ok: r.ok, data: j }; });
    });
  }
  function codeApplicable(data, code) {
    var codes = data && data.discount_codes;
    if (!codes || typeof codes.some !== 'function') return null;   // field absent -> decide from the section render
    return codes.some(function (c) { return String(c.code || '').toLowerCase() === code.toLowerCase() && c.applicable !== false; });
  }
  function applyDiscount() {
    var input = document.getElementById('AtelierDiscInput');
    var code = input ? input.value.trim() : '';
    if (!code) return;
    var btn = document.getElementById('AtelierDiscBtn'); if (btn) btn.disabled = true;
    inflight++; refreshBar(); setDiscMsg('');
    flush();   // unsaved quantities go first, so the code is judged on the cart the customer sees
    serial(function () { return postDiscount(code); })
      .then(function (res) {
        var data = res.data;
        if (!res.ok || !data) {
          setDiscMsg((data && (data.description || data.message)) || 'Could not apply that code. Please try again.', true);
          return;
        }
        if (data.sections && data.sections['atelier-cart']) applySection(data.sections['atelier-cart'], { noAnim: true });
        requote();   // the total moved - the free-shipping rule may have flipped
        var pill = drawer.querySelector('[data-ct-disc-row]');
        var known = codeApplicable(data, code);
        var ok = (known === null) ? !!pill : known;
        if (ok && pill) {
          /* one-time voucher entrance + sheen (CSS .just-on); class lifted
             after the animation so overflow never clips the tap window at rest */
          pill.classList.add('just-on');
          setTimeout(function () { pill.classList.remove('just-on'); }, 1300);
          closeTools();                       // label now carries the code; the summary row shows it applied
        } else if (ok) {
          /* valid per the API but invisible to cart Liquid (shipping-only):
             keep it on the cart and say where it lands */
          var lbl = document.getElementById('AtelierDiscLbl'); if (lbl) lbl.textContent = code.toUpperCase();
          setDiscMsg('Code ' + code.toUpperCase() + ' will be applied at checkout.');
        } else {
          /* an inapplicable code may still be stored on the cart - take it
             back off FIRST; the message is written after the cleanup's
             re-render lands, or that swap wipes it (and the typed code) */
          serial(function () { return postDiscount(''); }).then(function (res2) {
            var d2 = res2.data;
            if (d2 && d2.sections && d2.sections['atelier-cart']) applySection(d2.sections['atelier-cart'], { noAnim: true });
          }).catch(function () { /* section already shows no discount */ })
            .then(function () {
              var inp = document.getElementById('AtelierDiscInput');
              if (inp && !inp.value) inp.value = code;
              /* Shopify stores unknown codes as "not applicable" rather than
                 saying they don't exist, so cover both honestly */
              setDiscMsg('That code isn\'t valid or can\'t be used on this cart. Check the spelling and try again.', true);
            });
        }
      })
      .catch(function (e2) { setDiscMsg('Could not apply that code. Please try again.', true); console.error('[atelier-cart] discount', e2); })
      .finally(function () { var b = document.getElementById('AtelierDiscBtn'); if (b) b.disabled = false; inflight--; refreshBar(); });
  }
  /* Taking a code off is shown on the tap: the code's row goes at once, and the
     figures it touched wait as placeholders (paintUnsure) - without the code
     another discount may apply, which only Shopify knows. A failed removal
     redraws Shopify's cart, code and all. */
  function removeDiscount(refocus) {
    if (discGoing) return;
    inflight++; refreshBar(); setDiscMsg('');
    discGoing = true;
    var row = drawer.querySelector('[data-ct-disc-row]'), parent = row && row.parentNode, next = row && row.nextSibling;
    if (row) row.remove();
    recompute();
    function putBack() {   // the code is still on the cart: show it again at once, then Shopify's cart
      discGoing = false;
      if (row && parent && parent.isConnected && !drawer.querySelector('[data-ct-disc-row]')) parent.insertBefore(row, next && next.parentNode === parent ? next : null);
      recompute();
      refresh(false, true);
    }
    flush();
    serial(function () { return postDiscount(''); })
      .then(function (res) {
        if (!res.ok || !res.data) { putBack(); return; }
        discGoing = false;
        var html = res.data.sections && res.data.sections['atelier-cart'];
        if (html) applySection(html, { noAnim: true }); else refresh(false, true);
        requote();
        if (refocus) { var inp = document.getElementById('AtelierDiscInput'); if (inp) inp.focus(); }
      })
      .catch(function (e2) { console.error('[atelier-cart] discount remove', e2); putBack(); })
      .finally(function () { inflight--; refreshBar(); });
  }

  /* ---------- shipping estimate (suburb/postcode search → live carrier rate) ---------- */
  /* Lazy-loaded AU localities ("Suburb|POSTCODE|STATE") for the search field. */
  var LOCS = null, locsLoading = null;
  function loadLocs() {
    if (LOCS) return Promise.resolve(LOCS);
    if (locsLoading) return locsLoading;
    // Loaded via <script> (not fetch) so it works cross-origin from the CDN
    // on both the dev preview and live — no CORS / mixed-content issues.
    locsLoading = new Promise(function (resolve) {
      if (window.atelierLocs) { LOCS = window.atelierLocs; resolve(LOCS); return; }
      var s = document.createElement('script');
      s.src = window.atelierLocalitiesUrl;
      s.onload = function () { LOCS = window.atelierLocs || []; resolve(LOCS); };
      s.onerror = function () { LOCS = []; console.warn('[atelier-cart] localities failed to load'); resolve(LOCS); };
      document.head.appendChild(s);
    });
    return locsLoading;
  }
  function searchLocs(q) {
    q = (q || '').trim().toLowerCase();
    if (q.length < 2 || !LOCS) return [];
    var byPc = /^\d+$/.test(q), res = [];
    for (var i = 0; i < LOCS.length && res.length < 8; i++) {
      var p = LOCS[i].split('|');                 // [suburb, postcode, state]
      var hay = byPc ? p[1] : p[0].toLowerCase();
      if (hay.indexOf(q) === 0) res.push(p);
    }
    return res;
  }
  function renderShipList(results) {
    var list = document.getElementById('AtelierShipList');
    if (!list) return;
    if (!results.length) { list.classList.remove('on'); list.innerHTML = ''; return; }
    list.innerHTML = results.map(function (p) {
      return '<li class="ct-ship-opt" role="option" data-s="' + esc(p[0]) + '" data-pc="' + p[1] + '" data-st="' + p[2] + '"><b>' + esc(p[0]) + '</b><span>' + p[2] + ' ' + p[1] + '</span></li>';
    }).join('');
    list.classList.add('on');
  }
  function fmtRate(p) { var n = parseFloat(p) || 0; return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)); }
  var TRUCK = '<svg class="ct-ship-tk" viewBox="0 0 24 24"><path d="M3 7h11v8H3z"/><path d="M14 10h4l3 3v2h-7z"/><circle cx="7.5" cy="17.5" r="1.6"/><circle cx="17.5" cy="17.5" r="1.6"/></svg>';
  var EDIT = '<svg class="ct-ship-edit" viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>';
  /* Delivery estimate lifecycle (Chris, 2026-09-11 - cart V2 round 2). Once a
     locality is known the estimate STAYS on screen and follows the cart: a qty
     change, a removal, an add or a discount change re-prices it in place
     (carrier rates depend on weight; the free-shipping rule on the total).
     While the new quote is in flight the old figure stays visible under a
     .stale flag so the UI can say "updating" instead of blanking or silently
     changing the total. Replaces the old resetShip() (which blanked the
     estimate after every flush and never touched the add path at all).
     - cartSig(): what the quote was priced for; same signature = no re-quote,
       so re-opening the drawer on an unchanged cart costs no carrier call.
     - shipSeq: a later request supersedes one that is still polling.
     - requote() is debounced and waits out an in-flight flush, so a burst of
       +/- taps prices the FINAL cart once. */
  function cartSig() {
    var s = [], disc = 0;
    if (!drawer) return '';
    drawer.querySelectorAll('.ct-item').forEach(function (it) { s.push(it.getAttribute('data-key') + ':' + it.getAttribute('data-qty')); });
    drawer.querySelectorAll('[data-ct-disc-row],[data-ct-auto-row],[data-ct-tier-row]').forEach(function (r) { disc += +r.getAttribute('data-disc') || 0; });
    return s.join('|') + '#' + disc;
  }
  function doRequote() {
    var ship = document.getElementById('AtelierShip');
    if (!drawer || !ship || drawer.classList.contains('is-empty')) return;
    var loc = shipLoc || loadLoc();
    if (!loc) return;                                                     // nowhere known yet - nothing to estimate
    if (ship.classList.contains('done') && shipSig === cartSig()) return;  // already priced for this exact cart
    pickLocality(loc.s, loc.pc, loc.st, true);
  }
  function requote() {
    clearTimeout(rqTimer);
    rqTimer = setTimeout(function () {
      if (busy()) { requote(); return; }
      doRequote();
    }, 120);
  }
  /* Shared "deliver to" locality (PDP freight estimator + cart page use the
     same key) - enter a suburb once anywhere, remembered everywhere. */
  function saveLoc(s, pc, st) {
    try { localStorage.setItem('atelierDeliverTo', JSON.stringify({ s: s, pc: pc, st: st })); } catch (e) { /* ignore */ }
  }
  function loadLoc() {
    try {
      var v = JSON.parse(localStorage.getItem('atelierDeliverTo') || 'null');
      if (v && v.s && v.pc && v.st) return v;
    } catch (e) { /* ignore */ }
    return null;
  }
  /* true when the cart holds only pick-up-only lines (shower screens) - see atelier-pickup.js */
  function nothingShips(scope, sel) {
    var its = scope ? scope.querySelectorAll(sel) : [];
    if (!its.length) return false;
    for (var i = 0; i < its.length; i++) if (!its[i].querySelector('[data-apk-line]')) return false;
    return true;
  }
  /* Poll the async rates endpoint until the carrier calc resolves
     (shipping_rates is null while computing, an array when done). */
  // Retry transient responses without converting them into a delivery refusal.
  function rateRequest(url, method) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller && setTimeout(function () { controller.abort(); }, 10000);
    return fetch(url, { method: method || 'GET', headers: { 'Accept': 'application/json' },
      cache: 'no-store', signal: controller ? controller.signal : undefined })
      .then(function (response) {
        if (!response.ok) throw new Error('Delivery request failed: ' + response.status);
        return method === 'POST' ? null : response.json();
      }).finally(function () { if (timer) clearTimeout(timer); });
  }
  function pollRates(attempt, addr, seq, started) {
    if (seq !== shipSeq) return Promise.resolve(null);
    started = started || Date.now();
    return rateRequest(rootUrl() + 'cart/async_shipping_rates.json?' + addr)
      .then(function (data) {
        if (seq !== shipSeq) return null;
        var rates = data ? data.shipping_rates : null;
        if (rates == null) {
          if (Date.now() - started >= 15000) throw new Error('Delivery calculation is still pending');
          return new Promise(function (resolve) { setTimeout(resolve, 750); })
            .then(function () { return pollRates(attempt + 1, addr, seq, started); });
        }
        if (!Array.isArray(rates)) throw new Error('Invalid delivery response');
        return rates.filter(function (rate) {
          return rate && rate.price != null && rate.price !== '' && isFinite(Number(rate.price)) && Number(rate.price) >= 0;
        });
      });
  }
  function freshRates(addr, seq, msg, attempt, started) {
    attempt = attempt || 0;
    started = started || Date.now();
    if (seq !== shipSeq) return Promise.resolve(null);
    if (Date.now() - started > 60000) return Promise.reject(new Error('Delivery quote timed out'));
    // Let quantity and discount updates reach Shopify before asking for a quote.
    if (busy() || discGoing) {
      return new Promise(function (resolve) { setTimeout(resolve, 300); })
        .then(function () { return freshRates(addr, seq, msg, attempt, started); });
    }
    var signature = cartSig();
    return rateRequest(rootUrl() + 'cart/prepare_shipping_rates.json?' + addr, 'POST')
      .then(function () { return seq === shipSeq ? pollRates(0, addr, seq) : null; })
      .then(function (rates) {
        if (seq !== shipSeq) return null;
        if (busy() || discGoing || signature !== cartSig()) throw new Error('Cart changed during quote');
        if (!rates || !rates.length) throw new Error('Delivery quote needs a refresh');
        return rates;
      }).catch(function (error) {
        if (seq !== shipSeq) return null;
        if (attempt >= 2) throw error;
        if (msg) msg.textContent = 'Refreshing your delivery estimate...';
        return new Promise(function (resolve) { setTimeout(resolve, 1000 * (attempt + 1)); })
          .then(function () { return freshRates(addr, seq, msg, attempt + 1, started); });
      });
  }
  function showDeliveryRetry(msg) {
    if (!msg) return;
    msg.textContent = 'Your delivery estimate is taking longer than usual. ';
    var button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('data-bsd-rate-retry', '');
    button.textContent = 'Refresh estimate';
    button.style.cssText = 'border:0;background:none;padding:0;font:inherit;color:inherit;text-decoration:underline;cursor:pointer';
    msg.appendChild(button);
    msg.appendChild(document.createTextNode(' or check delivery at checkout.'));
  }
  /* FREE SHIPPING promo - see snippets/atelier-freeship-rule. The rule object
     exists only while the promo is running, so both helpers are inert (and the
     extra cart.js read never happens) once it ends. */
  function fsTotalCents() {
    if (!window.BSD_FREESHIP) return Promise.resolve(null);
    return cartFetch(rootUrl() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { return c ? c.total_price : null; })
      .catch(function () { return null; });
  }
  function fsFree(pc, st, cents) {
    var FS = window.BSD_FREESHIP;
    return !!(FS && typeof cents === 'number' && FS.ok(pc, st, cents));
  }
  function pickLocality(suburb, pc, st, requoting) {
    saveLoc(suburb, pc, st);
    shipLoc = { s: suburb, pc: pc, st: st };
    var seq = ++shipSeq;
    var list = document.getElementById('AtelierShipList'); if (list) { list.classList.remove('on'); list.innerHTML = ''; }
    var q = document.getElementById('AtelierShipQ'); if (q) q.value = suburb + ', ' + st + ' ' + pc;
    var msg = document.getElementById('AtelierShipMsg'); msg.textContent = '';
    var ship = document.getElementById('AtelierShip');
    /* every line is a pick-up-only item (atelier-pickup-line rows): nothing is delivered, so no
       carrier call - an empty rate list would read as "we can't deliver to you" */
    if (nothingShips(drawer, '.ct-item')) {
      msg.textContent = 'Nothing in your cart needs delivery.';
      if (ship) ship.classList.remove('done', 'loading', 'stale');
      shipSig = null;
      return;
    }
    /* re-quote: keep the old result readable but flagged .stale (dimmed + spinner) */
    if (ship) { ship.classList.add('loading'); if (requoting && ship.classList.contains('done')) ship.classList.add('stale'); }
    inflight++; refreshBar();
    var addr = 'shipping_address%5Bzip%5D=' + encodeURIComponent(pc) +
      '&shipping_address%5Bcountry%5D=Australia&shipping_address%5Bprovince%5D=' + encodeURIComponent(st) +
      '&shipping_address%5Bcity%5D=' + encodeURIComponent(suburb);
    // Async flow: prepare kicks off the (possibly slow) carrier calc; then poll.
    Promise.all([freshRates(addr, seq, msg), fsTotalCents()])
      .then(function (a) { return { r: a[0], c: a[1] }; })
      .then(function (d) {
        if (seq !== shipSeq) return;   // a newer request superseded this one
        var rates = d.r || [], fsCents = d.c;
        /* no rate = no estimate: a stale figure must never pose as current */
        if (!rates.length) { showDeliveryRetry(msg); ship.classList.remove('done'); document.getElementById('AtelierShipLbl').textContent = 'Calculate delivery'; shipSig = null; return; }
        rates.sort(function (a, b) { return parseFloat(a.price) - parseFloat(b.price); });
        var r = rates[0];
        var free = parseFloat(r.price) === 0 || fsFree(pc, st, fsCents);
        var price = free ? 'FREE' : fmtRate(r.price);
        document.getElementById('AtelierShipRes').innerHTML =
          TRUCK + '<span class="ct-ship-rtxt">' + esc(r.name) + ' to <b>' + esc(suburb) + ' ' + pc + '</b></span>' +
          '<span class="ct-ship-rprice' + (free ? ' free' : '') + '">' + price + '</span>' + EDIT;
        document.getElementById('AtelierShip').classList.add('done');
        document.getElementById('AtelierShipLbl').textContent = pc + ' · ' + price;
        shipSig = cartSig();
      })
      .catch(function (e) { if (seq !== shipSeq) return; showDeliveryRetry(msg); ship.classList.remove('done'); document.getElementById('AtelierShipLbl').textContent = 'Calculate delivery'; shipSig = null; console.warn('[atelier-cart] Estimate unavailable after automatic refresh', e.message); })
      .finally(function () { if (ship && seq === shipSeq) { ship.classList.remove('loading'); ship.classList.remove('stale'); paintUnsure(); } inflight--; refreshBar(); });
  }

  /* ---------- delegated events ---------- */
  function onClick(e) {
    if (e.target.closest('[data-bsd-rate-retry]')) { var retryLoc = shipLoc || loadLoc(); if (retryLoc) pickLocality(retryLoc.s, retryLoc.pc, retryLoc.st); return; }
    if (e.target.closest('#AtelierCartClose') || e.target.closest('.ct-cont')) { close(); return; }

    /* "View full cart" — a <button> (not an <a href="/cart">) so slide-cart apps
       (FKcart) can't hijack the click; we navigate ourselves. */
    var viewCart = e.target.closest('[data-ct-viewcart]');
    if (viewCart) {
      var to = viewCart.getAttribute('data-href') || '/cart';
      if (!leaveWhenSaved(e, viewCart, to)) window.location.href = to;
      return;
    }

    /* checkout with an edit still on its way: save it first (nothing waiting =
       the link works exactly as before). The pick-up-only check in
       atelier-pickup.js runs earlier (document capture) and stops this click
       when it has to. */
    var checkout = e.target.closest('#AtelierCheckout');
    if (checkout) { leaveWhenSaved(e, checkout, checkout.href || '/checkout'); return; }

    var inc = e.target.closest('[data-ct-plus]'), dec = e.target.closest('[data-ct-minus]'), rm = e.target.closest('[data-ct-remove]');
    if (inc || dec || rm) {
      var li = e.target.closest('.ct-item'); if (!li) return;
      if (li.classList.contains('removing')) return;   // already on its way out: a second tap must not bring it back
      var key = li.getAttribute('data-key');
      var q = parseInt(li.getAttribute('data-qty'), 10) || 1;
      var nq = inc ? q + 1 : (rm ? 0 : q - 1);
      /* queued first, so the recompute knows this edit is on its way */
      if (nq <= 0) {
        li.setAttribute('data-qty', '0');
        li.classList.add('removing');
        queue(key, 0);
        recompute();   // the totals move on the tap; the row leaves after its animation
        setTimeout(function () { li.remove(); recompute(); }, 180);
      } else {
        li.setAttribute('data-qty', nq);
        var nEl = li.querySelector('.ct-qty .n'); if (nEl) nEl.textContent = nq;
        updateLine(li);
        queue(key, nq);
        recompute();
      }
      return;
    }

    var moreBtn = e.target.closest('.ct-more-btn');
    if (moreBtn) {
      var props = moreBtn.closest('.ct-props');
      var isOpen = props.classList.toggle('open');
      moreBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      moreBtn.textContent = isOpen ? 'Show less' : moreBtn.getAttribute('data-more');
      return;
    }

    var noteBtn = e.target.closest('.ct-note-btn');
    if (noteBtn) {
      var note = document.getElementById('AtelierCartNote'); note.classList.add('open');
      var ta = note.querySelector('textarea'); if (ta) ta.focus();
      return;
    }

    var tool = e.target.closest('.ct-tool');
    if (tool) {
      var t = tool.dataset.tool, isOn = tool.classList.contains('on');
      drawer.querySelectorAll('.ct-tool').forEach(function (x) { x.classList.toggle('on', x === tool && !isOn); });
      drawer.querySelectorAll('.ct-tool-panel').forEach(function (p) { p.classList.toggle('on', p.dataset.panel === t && !isOn); });
      document.getElementById('AtelierToolWrap').classList.toggle('open', !isOn);
      if (!isOn) { var inp = drawer.querySelector('.ct-tool-panel[data-panel="' + t + '"] input'); if (inp) setTimeout(function () { inp.focus(); }, 260); }
      /* Ship tool opened with a remembered locality (saved here or on a PDP)
         and no rate yet -> quote it straight away. */
      if (!isOn && t === 'ship') {
        var shipEl = document.getElementById('AtelierShip');
        var shipQ = document.getElementById('AtelierShipQ');
        var savedLoc = loadLoc();
        if (savedLoc && shipEl && !shipEl.classList.contains('done') && !shipEl.classList.contains('loading') && shipQ && !shipQ.value) {
          pickLocality(savedLoc.s, savedLoc.pc, savedLoc.st);
        }
      }
      return;
    }

    var shipOpt = e.target.closest('.ct-ship-opt');
    if (shipOpt) { pickLocality(shipOpt.getAttribute('data-s'), shipOpt.getAttribute('data-pc'), shipOpt.getAttribute('data-st')); return; }

    var shipRes = e.target.closest('.ct-ship-res');
    if (shipRes) { document.getElementById('AtelierShip').classList.remove('done'); var q = document.getElementById('AtelierShipQ'); if (q) { q.value = ''; q.focus(); } return; }

    var discBtn = e.target.closest('#AtelierDiscBtn');
    if (discBtn) { applyDiscount(); return; }

    /* shared by the summary-row pill's x and the panel's Remove button;
       refocus the input only when removing from inside the panel */
    var discRm = e.target.closest('[data-ct-disc-rm]');
    if (discRm) { removeDiscount(!!discRm.closest('.ct-tool-panel')); return; }
  }
  function onSubmit() { /* no <form> in the drawer — shipping is selection-driven */ }
  var debouncedNote = debounce(function (v) { saveNote(v); }, 450);
  var debouncedSearch = debounce(function (v) { loadLocs().then(function () { renderShipList(searchLocs(v)); }); }, 120);
  function onInput(e) {
    if (e.target.id === 'AtelierShipQ') { debouncedSearch(e.target.value); return; }
    if (e.target.closest('#AtelierCartNote') && e.target.tagName === 'TEXTAREA') { debouncedNote(e.target.value); }
  }

  /* ---------- global add-to-cart interception ---------- */
  var adding = false;
  document.addEventListener('submit', function (e) {
    if (!window.atelierCartOn) return;
    var form = e.target;
    if (!form || form.nodeName !== 'FORM') return;
    if ((form.getAttribute('action') || '').indexOf('/cart/add') === -1) return;
    if (e.defaultPrevented) return;
    e.preventDefault();
    if (adding) return;
    adding = true; inflight++; refreshBar();
    var btn = form.querySelector('[type="submit"]');
    if (btn) btn.classList.add('loading');
    var fd = new FormData(form);
    fd.append('sections', 'atelier-cart');
    fd.append('sections_url', location.pathname);
    /* waits its turn behind any quantity save, so the two never overlap */
    serial(function () { return cartFetch(rootUrl() + 'cart/add.js', { method: 'POST', headers: { 'Accept': 'application/json' }, body: fd }); })
      .then(function (r) {
        if (btn) btn.classList.remove('loading');
        if (!r.ok) { console.error('[atelier-cart] add http', r.status); return null; }
        return r.json();
      })
      .then(function (data) {
        if (!data) return;
        if (data.status) { console.error('[atelier-cart] add rejected', data); return; }
        if (data.sections && data.sections['atelier-cart']) applySection(data.sections['atelier-cart']);
        show();
        syncBadgeFromDrawer();
        scheduleSettle();
        requote();
      })
      .catch(function (err) { if (btn) btn.classList.remove('loading'); console.error('[atelier-cart] add', err); })
      .finally(function () { adding = false; inflight--; refreshBar(); });
  });

  /* open the drawer. If passed pre-rendered section HTML (e.g. from a /cart/add.js `sections`
     response), apply it directly instead of doing a second fetch - saves a round-trip.
     Called with no argument everywhere else, so behaviour is unchanged for those callers. */
  var lastOpenAt = 0;
  window.openAtelierCart = function (html) {
    if (!bind()) return;
    /* the theme's add paths open the drawer TWICE, ~25 ms apart (cart-drawer.js +
       the add code itself). Two real adds cannot both finish inside 150 ms, so a
       second plain open that soon is the same add: let the read already on its way
       (started after the add landed) show it, rather than start over (~0.3 s sooner) */
    var now = Date.now(), dup = !(typeof html === 'string' && html) && now - lastOpenAt < 150;
    lastOpenAt = now;
    if (dup) { show(); return; }
    epoch++;   // the cart was just changed elsewhere (a product-page add): any read in flight is old
    show();
    if (typeof html === 'string' && html) { applySection(html); cleanupOrphans().then(requote); }
    else { refresh().then(function () { cleanupOrphans().then(requote); }); }
    /* opens usually follow a mutation (PDP add routes here) - settle-check so
       a tier CornerCart applies moments later still lands in the open drawer */
    scheduleSettle();
  };

  /* CornerCart's sanctioned JS event (same corner.on hook main.js has used
     for the VB safeguards since the CornerCart-drawer era): onCartEdit fires
     as its engine processes cart edits - INCLUDING its own attribute write
     that (re)applies the tier offer (main.js's re-entry guard proves its own
     edits re-fire it). Refreshing here, debounced + quiet, lands the
     milestone row ~1s after a crossing instead of waiting for the timed
     probes; the probes above stay as the fallback when the event is late or
     the embed absent. A GET refresh is not a cart edit, so no loop. */
  var cornerRefresh = debounce(function () {
    if (!drawer) return;
    if (!goalCampaign() && !drawer.querySelector('[data-ct-tier-row]')) return;
    if (busy()) return;
    refresh(true);
  }, 250);
  (function hookCorner(tries) {
    if (window.corner && typeof window.corner.on === 'function') {
      try { window.corner.on('onCartEdit', function () { cornerRefresh(); }); }
      catch (e) { console.warn('[atelier-cart] corner hook', e); }
      return;
    }
    if (tries > 0) setTimeout(function () { hookCorner(tries - 1); }, 150);
  })(200);

  if (document.readyState !== 'loading') bind();
  else document.addEventListener('DOMContentLoaded', bind);
})();

